import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { intlTag } from '@/lib/i18n'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * `Intl` objects are expensive to construct and these run inside virtualised
 * list rows, so keep one per locale rather than one per call. The active locale
 * comes from i18next, so switching language switches the separators, the month
 * order and the sort order together.
 */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.Collator>()

function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.Collator>(
  key: string,
  make: () => T,
): T {
  let hit = cache.get(key)
  if (!hit) cache.set(key, (hit = make()))
  return hit as T
}

export function formatNumber(n: number | null | undefined) {
  if (typeof n !== 'number') return '—'
  const tag = intlTag()
  return memo(`n:${tag}`, () => new Intl.NumberFormat(tag)).format(n)
}

/** Date only — for "last seen" style stamps where the time adds nothing. */
export function formatDate(value: string | number | Date | null | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const tag = intlTag()
  return memo(`d:${tag}`, () => new Intl.DateTimeFormat(tag, { dateStyle: 'medium' })).format(d)
}

/** Date and time — for expiry and start stamps, where the hour matters. */
export function formatDateTime(value: string | number | Date | null | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const tag = intlTag()
  return memo(
    `dt:${tag}`,
    () => new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }),
  ).format(d)
}

/**
 * Locale-aware string comparison for A–Z sorting. Estonian orders Š/Z/Ž/Õ/Ä/Ö/Ü
 * after W, Finnish orders Å/Ä/Ö after Z — a plain `<` gets both wrong.
 */
export function compareStrings(a: string, b: string) {
  const tag = intlTag()
  return memo(`c:${tag}`, () => new Intl.Collator(tag)).compare(a, b)
}

/** Escape a string for safe use inside a RegExp. */
export function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Split `text` into segments, marking those that match `needle` on a word
 * boundary. Mirrors the Python `_highlight()` in the Panel view, but returns
 * data instead of HTML so React can render it without dangerouslySetInnerHTML.
 */
export function highlightSegments(text: string, needle: string) {
  if (!needle || !text) return [{ text, match: false }]
  let re: RegExp
  try {
    re = new RegExp(`(?<!\\p{L})${escapeRegExp(needle)}(?!\\p{L})`, 'giu')
  } catch {
    return [{ text, match: false }]
  }
  const out: { text: string; match: boolean }[] = []
  let last = 0
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0
    if (i > last) out.push({ text: text.slice(last, i), match: false })
    out.push({ text: m[0], match: true })
    last = i + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last), match: false })
  return out.length ? out : [{ text, match: false }]
}

/**
 * Split `text` on every case-insensitive occurrence of `needle`, for
 * search-result highlighting.
 *
 * Distinct from `highlightSegments`, which anchors on word boundaries. That is
 * right for marking term occurrences in running prose, but wrong here: term
 * search matches on plain substring (`value.includes(q)`), so a query like
 * "tasakaal" finds "tasakaalustamisenergia" — and a word-boundary highlighter
 * would show that hit with nothing marked.
 */
export function highlightMatch(text: string, needle: string) {
  const q = needle.trim()
  if (!q || !text) return [{ text, match: false }]
  const out: { text: string; match: boolean }[] = []
  const hay = text.toLowerCase()
  const low = q.toLowerCase()
  let from = 0
  for (;;) {
    const at = hay.indexOf(low, from)
    if (at === -1) break
    if (at > from) out.push({ text: text.slice(from, at), match: false })
    out.push({ text: text.slice(at, at + low.length), match: true })
    from = at + low.length
  }
  if (!out.length) return [{ text, match: false }]
  if (from < text.length) out.push({ text: text.slice(from), match: false })
  return out
}

export interface TermSegment {
  text: string
  /** Key of the term this segment matched, or null for plain text. */
  termKey: string | null
}

/**
 * Split a document into segments, tagging every occurrence of any known term
 * surface with the term it belongs to.
 *
 * One combined regex rather than a pass per term: with several hundred terms
 * over ~90k characters, per-term passes would be O(terms × text). Longer
 * surfaces are ordered first so "suur keelemudel" wins over "keelemudel".
 */
export function highlightTerms(
  text: string,
  surfaceToKey: Map<string, string>,
): TermSegment[] {
  if (!text) return []
  if (!surfaceToKey.size) return [{ text, termKey: null }]

  const surfaces = [...surfaceToKey.keys()]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)

  let re: RegExp
  try {
    re = new RegExp(`(?<!\\p{L})(${surfaces.join('|')})(?!\\p{L})`, 'giu')
  } catch {
    return [{ text, termKey: null }]
  }

  const out: TermSegment[] = []
  let last = 0
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0
    if (i > last) out.push({ text: text.slice(last, i), termKey: null })
    out.push({ text: m[0], termKey: surfaceToKey.get(m[0].toLowerCase()) ?? null })
    last = i + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last), termKey: null })
  return out
}

/**
 * The text-directive form of a phrase (`:~:text=…`). Besides the usual
 * percent-encoding, a hyphen has to be encoded too: in a text directive a
 * bare `-` separates the prefix/suffix parts, so "e-mail" would otherwise
 * be read as a range.
 */
export function textFragment(surface: string) {
  return `:~:text=${encodeURIComponent(surface.trim()).replace(/-/g, '%2D')}`
}

/**
 * Build a source link that jumps straight to the surface form using a text
 * fragment, the same trick the Panel popup used.
 */
export function sourceLink(url: string | null | undefined, surface?: string | null) {
  if (!url) return null
  return surface?.trim() ? `${url}#${textFragment(surface)}` : url
}
